import React, { useState } from 'react';
import { Alert, Button, Typography, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import api from '../../authentication/axiosInstance';
import { PHOTO_ACCEPT, uploadRequestPhotos, validatePhotoFile } from '../../utils/requestPhotoUpload';

const { Text } = Typography;

// Accepts a file, or a failed result ({ file, resume, error }) to retry.
const toListItem = (entry) => {
  const { file, resume = null, error } = entry instanceof Blob ? { file: entry } : entry;
  return {
    uid: file.uid || file.name,
    name: file.name,
    originFileObj: file,
    resume,
    ...(error ? { status: 'error', response: error } : {}),
  };
};

const RequestPhotoUploader = ({ requestId, initialFiles = [], onFinished }) => {
  const [files, setFiles] = useState(() => initialFiles.map(toListItem));
  const [uploading, setUploading] = useState(false);

  const failed = files.filter((item) => item.status === 'error');

  const handleUpload = async () => {
    setUploading(true);
    try {
      const results = await uploadRequestPhotos(
        api,
        requestId,
        files.map((item) => ({ file: item.originFileObj, resume: item.resume })),
      );
      const failures = new Map(results.filter((result) => !result.ok).map((result) => [result.file, result]));
      const uploaded = results.length - failures.size;
      // Keep only the failures, so retrying never re-sends a verified photo,
      // and keep how far each got so stored files are only re-verified.
      setFiles((current) => current
        .filter((item) => failures.has(item.originFileObj))
        .map((item) => toListItem(failures.get(item.originFileObj))));
      if (uploaded) message.success(`${uploaded} photo${uploaded === 1 ? '' : 's'} uploaded.`);
      if (failures.size) message.error(`${failures.size} photo${failures.size === 1 ? '' : 's'} failed to upload.`);
      if (onFinished) onFinished({ uploaded, remaining: failures.size });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="request-photo-uploader">
      <Upload
        multiple
        accept={PHOTO_ACCEPT}
        listType="picture"
        fileList={files}
        disabled={uploading}
        beforeUpload={(file) => {
          const problem = validatePhotoFile(file);
          if (problem) {
            message.error(problem);
            return Upload.LIST_IGNORE;
          }
          setFiles((current) => [...current, toListItem(file)]);
          return false;
        }}
        onRemove={(file) => setFiles((current) => current.filter((item) => item.uid !== file.uid))}
      >
        <Button icon={<UploadOutlined />} disabled={uploading}>Select File</Button>
      </Upload>
      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
        JPEG, PNG, or WebP, up to 10 MB each.
      </Text>
      {failed.length > 0 && (
        <Alert
          type="error"
          style={{ marginTop: 12 }}
          message="Some photos were not uploaded"
          description={(
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {failed.map((item) => <li key={item.uid}>{item.name}: {item.response}</li>)}
            </ul>
          )}
        />
      )}
      <Button
        type="primary"
        onClick={handleUpload}
        disabled={files.length === 0}
        loading={uploading}
        style={{ marginTop: 16 }}
      >
        {uploading ? 'Uploading' : failed.length ? 'Retry Upload' : 'Start Upload'}
      </Button>
    </div>
  );
};

export default RequestPhotoUploader;
